import { X, CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react";
import { useEffect } from "react";
import Loader from "../Loader";

export default function TransactionDetailsModal({ 
  isOpen, 
  onClose, 
  transaction, 
  loading 
}) {
  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Normalize transaction data - handle both summary format and history format
  const normalizeTransaction = (txn) => {
    if (!txn) return null;
    
    // If it's already in summary format, return as is
    if (txn.financial_summary || txn.business_earnings) {
      return txn;
    }
    
    // If it's in history format, normalize it
    return {
      uuid: txn.uuid,
      mobile: txn.transaction_details?.mobile,
      status: txn.transaction_details?.status,
      status_label: txn.transaction_details?.status_label,
      created_at: txn.transaction_details?.created_at,
      fulfilled_at: txn.transaction_details?.fulfilled_at,
      customer: txn.customer_info ? {
        name: txn.customer_info.name,
        mobile: txn.customer_info.mobile
      } : null,
      financial_summary: txn.financial_details ? {
        billable_amount: txn.financial_details.billable_amount,
        customer_savings: txn.financial_details.customer_discount,
        platform_commission: txn.financial_details.company_commission,
        final_amount: txn.financial_details.final_amount
      } : null,
      discount_details: txn.discount_info ? {
        discount_text: txn.discount_info.discount_text,
        discount_id: txn.discount_info.discount_id
      } : null,
      business_earnings: txn.calculations ? {
        gross_amount: txn.financial_details?.billable_amount?.amount || '0',
        customer_discount_given: txn.calculations.savings_for_customer,
        platform_commission_paid: txn.calculations.commission_to_platform,
        net_received: txn.calculations.earnings_for_business
      } : null
    };
  };

  const normalizedTransaction = normalizeTransaction(transaction);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'FULFILLED':
        return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'CANCELLED':
        return <XCircle className="h-6 w-6 text-red-600" />;
      case 'INITIATED':
        return <Clock className="h-6 w-6 text-blue-600" />;
      default:
        return <AlertCircle className="h-6 w-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'FULFILLED': return 'text-green-600 bg-green-50 border-green-200';
      case 'CANCELLED': return 'text-red-600 bg-red-50 border-red-200';
      case 'INITIATED': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Close modal when clicking on backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-[calc(100%-2rem)] max-h-[92vh] sm:max-h-[90vh] overflow-hidden sm:w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Transaction Details</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] touch-manipulation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(92vh-140px)] sm:max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <Loader size="lg" />
              <span className="ml-3 text-gray-600 text-sm sm:text-base">Loading transaction details...</span>
            </div>
          ) : !normalizedTransaction ? (
            <div className="text-center py-8 sm:py-12">
              <AlertCircle className="h-10 w-10 sm:h-12 sm:w-12 text-red-500 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                Unable to load transaction details
              </h3>
              <p className="text-gray-600 text-sm sm:text-base px-2">
                There was an error loading the transaction information.
              </p>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {/* Status Header */}
              <div className="text-center">
                <div className="mb-3 sm:mb-4">
                  {getStatusIcon(normalizedTransaction.status)}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  Transaction {normalizedTransaction.status_label || normalizedTransaction.status}
                </h3>
                <span className={`inline-flex px-3 py-1 text-xs sm:text-sm font-semibold rounded-full border ${getStatusColor(normalizedTransaction.status)}`}>
                  {normalizedTransaction.status_label || normalizedTransaction.status}
                </span>
              </div>

              {/* Basic Transaction Info */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <h4 className="font-semibold text-gray-900 mb-3 text-sm sm:text-base">Transaction Information</h4>
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Transaction ID</p>
                    <p className="font-mono text-xs sm:text-sm text-gray-900 break-all">{normalizedTransaction.uuid}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Mobile Number</p>
                    <p className="text-xs sm:text-sm text-gray-900">{normalizedTransaction.mobile}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Created At</p>
                    <p className="text-xs sm:text-sm text-gray-900">{formatDate(normalizedTransaction.created_at)}</p>
                  </div>
                  {normalizedTransaction.fulfilled_at && (
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {normalizedTransaction.status === 'FULFILLED' ? 'Completed At' : 'Updated At'}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-900">{formatDate(normalizedTransaction.fulfilled_at)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Information */}
              {normalizedTransaction.customer && (
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                  <h4 className="font-semibold text-blue-900 mb-3 text-sm sm:text-base">Customer Information</h4>
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs sm:text-sm text-blue-700">Name</p>
                      <p className="font-medium text-blue-900 text-sm sm:text-base">{normalizedTransaction.customer.name}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-blue-700">Mobile</p>
                      <p className="text-xs sm:text-sm text-blue-900">{normalizedTransaction.customer.mobile}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Financial Summary */}
              {normalizedTransaction.financial_summary && (
                <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                  <h4 className="font-semibold text-green-900 mb-3 text-sm sm:text-base">Financial Summary</h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700 text-xs sm:text-sm">Billable Amount:</span>
                      <span className="font-medium text-green-900 text-sm sm:text-base">
                        {normalizedTransaction.financial_summary.billable_amount.formatted}
                      </span>
                    </div>

                    {normalizedTransaction.financial_summary.customer_savings && (
                      <div className="flex justify-between items-center">
                        <span className="text-green-700 text-xs sm:text-sm">Customer Savings:</span>
                        <span className="font-medium text-green-600 text-sm sm:text-base">
                          -{normalizedTransaction.financial_summary.customer_savings.formatted}
                        </span>
                      </div>
                    )}

                    <div className="border-t border-green-200 pt-2 sm:pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-base sm:text-lg font-semibold text-green-900">Final Amount:</span>
                        <span className="text-lg sm:text-xl font-bold text-green-600">
                          {normalizedTransaction.financial_summary.final_amount.formatted}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Business Details */}
              {normalizedTransaction.business && (
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm sm:text-base">Business Information</h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Business Name</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">{normalizedTransaction.business.business_name}</p>
                    </div>
                    {normalizedTransaction.business.address && (
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600">Address</p>
                        <p className="text-xs sm:text-sm text-gray-900">{normalizedTransaction.business.address}</p>
                      </div>
                    )}
                    {normalizedTransaction.business.whatsapp_number && (
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600">WhatsApp</p>
                        <p className="text-xs sm:text-sm text-gray-900">{normalizedTransaction.business.whatsapp_number}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors min-h-[44px] touch-manipulation"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}